import { lumi } from '@/lib/lumi'
import { isEthAddress, isNonZeroAddress } from '@/lib/launchpad/contracts'

export type ExplorerHolder = { address: string, amount: bigint }

type ExplorerHoldersResult = {
  holders: ExplorerHolder[]
  contributions: ExplorerHolder[]
}

const cache = new Map<string, ExplorerHoldersResult>()
const inflight = new Map<string, Promise<ExplorerHoldersResult | undefined>>()

function parseAmount(value: unknown): bigint | undefined {
  if (typeof value === 'bigint' && value > 0n)
    return value
  if (typeof value === 'number' && Number.isFinite(value) && value > 0)
    return BigInt(Math.trunc(value))
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (/^\d+$/.test(trimmed)) {
      const amount = BigInt(trimmed)
      return amount > 0n ? amount : undefined
    }
    if (/^0x[0-9a-fA-F]+$/.test(trimmed)) {
      try {
        const amount = BigInt(trimmed)
        return amount > 0n ? amount : undefined
      }
      catch {
        return undefined
      }
    }
  }
  return undefined
}

function parseRows(list: unknown): ExplorerHolder[] {
  if (!Array.isArray(list))
    return []
  const rows: ExplorerHolder[] = []
  for (const item of list) {
    if (!item || typeof item !== 'object')
      continue
    const row = item as {
      address?: unknown
      TokenHolderAddress?: unknown
      hash?: unknown
      amount?: unknown
      TokenHolderQuantity?: unknown
      value?: unknown
    }
    const nested = row.address && typeof row.address === 'object'
      ? (row.address as { hash?: unknown }).hash
      : undefined
    const address = [row.address, row.TokenHolderAddress, nested, row.hash]
      .find((value): value is string => typeof value === 'string')
    const amount = parseAmount(row.amount ?? row.TokenHolderQuantity ?? row.value)
    if (!isNonZeroAddress(address) || amount === undefined)
      continue
    rows.push({ address, amount })
  }
  return rows
}

function cacheKey(address?: string, token?: string) {
  return [address, token].filter(Boolean).join(':').toLowerCase()
}

export async function fetchExplorerHolders(options: {
  address?: string
  token?: string
}): Promise<ExplorerHoldersResult | undefined> {
  const address = isEthAddress(options.address) ? options.address : undefined
  const token = isEthAddress(options.token) ? options.token : undefined
  if (!address && !token)
    return undefined
  const key = cacheKey(address, token)
  const cached = cache.get(key)
  if (cached)
    return cached
  const pending = inflight.get(key)
  if (pending)
    return pending

  const promise = (async () => {
    try {
      const result = await lumi.functions.invoke('tokenHolders', {
        method: 'POST',
        body: {
          token: token || address,
        },
      })
      const record = (result && typeof result === 'object' ? result : {}) as {
        ok?: unknown
        holders?: unknown
        contributions?: unknown
        result?: unknown
        items?: unknown
        data?: unknown
      }
      const nested = record.data && typeof record.data === 'object'
        ? record.data as { holders?: unknown, contributions?: unknown, result?: unknown, items?: unknown }
        : undefined
      if (record.ok === false)
        return undefined
      const parsed: ExplorerHoldersResult = {
        holders: parseRows(record.holders ?? nested?.holders ?? record.items ?? nested?.items ?? record.result ?? nested?.result),
        contributions: parseRows(record.contributions ?? nested?.contributions),
      }
      if (parsed.holders.length > 0 || parsed.contributions.length > 0)
        cache.set(key, parsed)
      return parsed
    }
    catch {
      return undefined
    }
    finally {
      inflight.delete(key)
    }
  })()

  inflight.set(key, promise)
  return promise
}

export async function fetchExplorerTokenHolders(token?: string): Promise<ExplorerHolder[] | undefined> {
  const result = await fetchExplorerHolders({ token })
  return result?.holders
}

export function invalidateExplorerTokenHolders(token?: string) {
  if (!isEthAddress(token))
    return
  const key = token.toLowerCase()
  cache.delete(key)
  inflight.delete(key)
  for (const cachedKey of [...cache.keys()]) {
    if (cachedKey.includes(key))
      cache.delete(cachedKey)
  }
}

export type ExplorerWalletBalance = { ethWei?: bigint, tokenAmount?: bigint }

const walletInflight = new Map<string, Promise<ExplorerWalletBalance | undefined>>()

function walletCacheKey(token: string, wallet: string) {
  return `${token.toLowerCase()}::${wallet.toLowerCase()}`
}

/**
 * Blockscout-backed cross-check for a single wallet's ETH balance and a
 * single token's balance for that wallet. This exists so the trade box's
 * "Wallet X ETH · holding Y TICKER" line never gets permanently stuck at 0
 * when the public RPC node used for the primary on-chain read is slow,
 * rate-limited, or briefly unavailable — the same class of failure that
 * previously caused holders to show empty before holders were made
 * Blockscout-only. Returns undefined fields (not "0") when a specific
 * lookup fails, so callers can keep showing the last known good value
 * instead of flashing to zero.
 */
export async function fetchExplorerWalletBalance(
  token?: string,
  wallet?: string,
): Promise<ExplorerWalletBalance | undefined> {
  if (!isEthAddress(token) || !isEthAddress(wallet))
    return undefined
  const key = walletCacheKey(token, wallet)
  const pending = walletInflight.get(key)
  if (pending)
    return pending

  const promise = (async () => {
    try {
      const result = await lumi.functions.invoke('tokenHolders', {
        method: 'POST',
        body: { token, wallet },
      })
      const record = (result && typeof result === 'object' ? result : {}) as {
        ok?: unknown
        wallet?: unknown
      }
      if (record.ok === false || !record.wallet || typeof record.wallet !== 'object')
        return undefined
      const walletRecord = record.wallet as { ethWei?: unknown, tokenAmount?: unknown }
      const ethWei = parseAmount(walletRecord.ethWei ?? undefined)
      const tokenAmount = parseAmount(walletRecord.tokenAmount ?? undefined)
      return { ethWei, tokenAmount }
    }
    catch {
      return undefined
    }
    finally {
      walletInflight.delete(key)
    }
  })()

  walletInflight.set(key, promise)
  return promise
}
